<template>
  <div class="mx-8">
    <settings-section
      :title="'Definir a redistribuição de leads'"
      :sub-title="'Defina a redistribuição de leads para o seu time'"
    >
      <form @submit.prevent="updateTeam">
        <label for="toggle-business-hours" class="toggle-input-wrap">
          <input
            v-model="isRedistributionEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-business-hours"
          />
          Ligar redistribuição de leads
        </label>
        <p class="text-slate-700 dark:text-slate-300 mb-4">
          Quando ativado, as conversas que não forem atendidas dentro do tempo
          configurado serão redistribuídas.
        </p>
        <div v-if="isRedistributionEnabled" class="mb-6">
          <div class="first-interaction-deadline-minutes-input-wrap">
            <label>Prazo para iniciar o atendimento</label>
            <multiselect
              v-model="selectedFirstInteractionDeadlineMinutes"
              :options="firstInteractionDeadlineMinutesOptions"
              deselect-label=""
              select-label=""
              selected-label=""
              track-by="value"
              label="label"
              :close-on-select="true"
              :placeholder="'Escolha'"
              :allow-empty="false"
            />
            <p class="text-slate-700 dark:text-slate-300 mb-4">
              Esse é o tempo limite para que um agente inicie o atendimento. Se
              passar desse tempo, a conversa será redistribuída.
            </p>
          </div>
          <label>Definir horários de ativação semanais</label>
          <business-day
            v-for="timeSlot in timeSlots"
            :key="timeSlot.day"
            :day-name="dayNames[timeSlot.day]"
            :time-slot="timeSlot"
            @update="data => onSlotUpdate(timeSlot.day, data)"
          />
        </div>
        <NextButton
          type="submit"
          label="Salvar"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </form>
    </settings-section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import BusinessDay from '../../../inbox/components/BusinessDay.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
} from '../../../inbox/helpers/businessHour';

const fiveMinutesFirstInteractionDeadlineMinutesOption = {
  label: '5 minutos',
  value: 5,
};

export default {
  components: {
    SettingsSection,
    BusinessDay,
    NextButton,
  },
  props: {
    team: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedFirstInteractionDeadlineMinutes:
        fiveMinutesFirstInteractionDeadlineMinutesOption,
      firstInteractionDeadlineMinutesOptions: [
        fiveMinutesFirstInteractionDeadlineMinutesOption,
        { label: '10 minutos', value: 10 },
        { label: '15 minutos', value: 15 },
        { label: '20 minutos', value: 20 },
        { label: '25 minutos', value: 25 },
        { label: '30 minutos', value: 30 },
        { label: '35 minutos', value: 35 },
        { label: '40 minutos', value: 40 },
        { label: '45 minutos', value: 45 },
        { label: '50 minutos', value: 50 },
        { label: '55 minutos', value: 55 },
        { label: '60 minutos', value: 60 },
        { label: '75 minutos', value: 75 },
        { label: '90 minutos', value: 90 },
        { label: '120 minutos', value: 120 },
        { label: '24 horas', value: 1440 },
      ],
      isRedistributionEnabled: false,
      dayNames: {
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday',
      },
      timeSlots: [...defaultTimeSlot],
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    hasError() {
      if (!this.isRedistributionEnabled) return false;
      return this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
    },
  },
  watch: {
    team() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        redistribution_enabled: isRedistributionEnabled = false,
        first_interaction_deadline_minutes: firstInteractionDeadlineMinutes = 5,
        working_hours: timeSlots = [],
      } = this.team;

      this.isRedistributionEnabled = isRedistributionEnabled;

      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;

      const selectedFirstInteractionDeadlineMinutes =
        this.firstInteractionDeadlineMinutesOptions.find(
          ({ value }) => value === firstInteractionDeadlineMinutes
        );

      this.selectedFirstInteractionDeadlineMinutes =
        selectedFirstInteractionDeadlineMinutes;
      this.timeSlots = slots;
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    async updateTeam() {
      try {
        const payload = {
          id: this.team.id,
          redistribution_enabled: this.isRedistributionEnabled,
          first_interaction_deadline_minutes:
            this.selectedFirstInteractionDeadlineMinutes.value,
          working_hours: timeSlotTransform(this.timeSlots),
        };
        await this.$store.dispatch('teams/update', payload);
        useAlert(
          'Configurações de redistribuição de leads do time atualizadas com sucesso'
        );
      } catch (error) {
        useAlert(
          error.message ||
            'Não foi possível atualizar as configurações de redistribuição de leads do time. Por favor, tente novamente mais tarde.'
        );
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.first-interaction-deadline-minutes-input-wrap {
  @apply max-w-[37.5rem];

  &::v-deep .multiselect {
    @apply mt-2;
  }
}
</style>
